import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Card, ListGroup, Badge, Alert } from "react-bootstrap";
import { FaUsers, FaSearch, FaArrowRight, FaCheck, FaFilter, FaCamera } from "react-icons/fa";

const GroupStudentTransfersModal = ({ show, onHide, schoolid, getStudentsBySchool, getClassesBySchoolId, transferStudents }) => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedTargetClass, setSelectedTargetClass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Filter states
  const [studentGradeFilter, setStudentGradeFilter] = useState("all");
  const [classGradeFilter, setClassGradeFilter] = useState("all");
  const [classAcademicYearFilter, setClassAcademicYearFilter] = useState("all");
  const [classSnapshotFilter, setClassSnapshotFilter] = useState("all");
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Available grade levels using boolean system
  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  // Academic years - will be populated from actual data
  const [availableAcademicYears, setAvailableAcademicYears] = useState([
    { value: "all", label: "All Academic Years" }
  ]);

  // Snapshot filter options
  const snapshotOptions = [
    { value: "all", label: "All Classes" },
    { value: "active", label: "Active Classes Only" },
    { value: "snapshot", label: "Snapshots Only" }
  ];

  const getGradeLabelFromClass = (classItem) => {
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  useEffect(() => {
    if (show && schoolid) {
      loadData();
    }
  }, [show, schoolid]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      // Get students and classes from your API
      const [studentsResponse, classesResponse] = await Promise.all([
        getStudentsBySchool(schoolid),
        getClassesBySchoolId(schoolid)
      ]);

      console.log("Students response:", studentsResponse); // Debug log
      console.log("Classes response:", classesResponse); // Debug log

      // Transform student data to match component expectations
      // Transform student data to match component expectations
      const transformedStudents = (studentsResponse || []).map(student => {
        const gradeLabel = student.class ? getGradeLabelFromClass(student.class) : 'Unknown Grade';
        const gradeBoolean = student.class ? getGradeBooleanFromClass(student.class) : 'unknown';

        // Parse surname from full name for sorting
        const nameParts = (student.name || '').trim().split(' ');
        const surname = nameParts.length > 1 ? nameParts[nameParts.length - 1] : student.name || '';
        const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : '';

        return {
          id: student.id,
          name: student.name,
          firstName: firstName, // Parsed from full name
          lastName: surname,    // Parsed from full name
          classId: student.classId,
          className: student.class?.name || 'Unknown Class',
          grade: gradeLabel,
          gradeBoolean: gradeBoolean
        };
      }).sort((a, b) => {
        // Sort alphabetically by surname (last name), then by first name
        const surnameComparison = a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase());
        if (surnameComparison !== 0) {
          return surnameComparison;
        }
        // If surnames are the same, sort by first name
        return a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase());
      });
      // Transform class data to match component expectations
      const transformedClasses = (classesResponse || []).map(classItem => {
        const gradeLabel = getGradeLabelFromClass(classItem);
        const gradeBoolean = getGradeBooleanFromClass(classItem);

        return {
          id: classItem.id,
          name: classItem.name,
          grade: gradeLabel,
          gradeBoolean: gradeBoolean,
          academicYear: classItem.academicYear, // Use direct field from API
          teacher: classItem.teacher?.name || classItem.teacherName || '',
          teacherId: classItem.teacherId,
          active: classItem.active,
          trial_class: classItem.trial_class,
          snapshot: classItem.snapshot || false, // Add snapshot field
          date_updated: classItem.date_updated,
          // Keep original boolean fields for reference
          grade_level1: classItem.grade_level1,
          grade_level2: classItem.grade_level2,
          grade_level3: classItem.grade_level3,
          grade_level4: classItem.grade_level4,
          grade_level5: classItem.grade_level5,
          grade_level6: classItem.grade_level6,
          grade_level7: classItem.grade_level7,
          grade_level8: classItem.grade_level8
        };
      }).sort((a, b) => {
        // Sort by academic year (newest first), then by name
        if (a.academicYear !== b.academicYear) {
          return b.academicYear?.localeCompare(a.academicYear || '') || 0;
        }
        return a.name.localeCompare(b.name);
      });

      // Extract unique academic years from the data for filter options
      const uniqueAcademicYears = [...new Set(transformedClasses
        .map(cls => cls.academicYear)
        .filter(year => year) // Remove null/undefined
      )].sort().reverse(); // Sort newest first

      const academicYearOptions = [
        { value: "all", label: "All Academic Years" },
        ...uniqueAcademicYears.map(year => ({
          value: year,
          label: `${year} Academic Year`
        }))
      ];

      setAvailableAcademicYears(academicYearOptions);

      console.log("Transformed students:", transformedStudents); // Debug log
      console.log("Transformed classes:", transformedClasses); // Debug log

      setStudents(transformedStudents);
      setClasses(transformedClasses);

    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudents(prev => {
      const isSelected = prev.find(s => s.id === student.id);
      if (isSelected) {
        return prev.filter(s => s.id !== student.id);
      } else {
        return [...prev, student];
      }
    });
  };

  const handleSelectAllStudents = () => {
    const filteredStudents = getFilteredStudents();
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents);
    }
  };

  const handleClassSelect = (classItem) => {
    setSelectedTargetClass(classItem);
  };

  const getFilteredStudents = () => {
    return students.filter(student => {
      const matchesGrade = studentGradeFilter === "all" || student.gradeBoolean === studentGradeFilter;
      const matchesSearch = student.name?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
        student.firstName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
        student.lastName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
        student.className?.toLowerCase().includes(studentSearchTerm.toLowerCase());
      return matchesGrade && matchesSearch;
    });
  };

  const getFilteredClasses = () => {
    return classes.filter(classItem => {
      const matchesGrade = classGradeFilter === "all" || classItem.gradeBoolean === classGradeFilter;
      const matchesAcademicYear = classAcademicYearFilter === "all" || classItem.academicYear === classAcademicYearFilter;

      // Snapshot filtering logic
      let matchesSnapshot = true;
      if (classSnapshotFilter === "active") {
        matchesSnapshot = !classItem.snapshot;
      } else if (classSnapshotFilter === "snapshot") {
        matchesSnapshot = classItem.snapshot;
      }
      // "all" matches everything, so matchesSnapshot stays true

      const matchesSearch = classItem.name?.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
        (classItem.teacher && classItem.teacher.toLowerCase().includes(classSearchTerm.toLowerCase()));
      return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesSearch;
    });
  };

  const handleTransferClick = () => {
    if (selectedStudents.length === 0 || !selectedTargetClass) {
      setError("Please select students and a target class.");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmTransfer = async () => {
    setShowConfirmModal(false);
    await handleTransfer();
  };

  const handleTransfer = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Use your bulk update API
      const response = await transferStudents({
        studentIds: selectedStudents.map(s => s.id),
        classId: selectedTargetClass.id
      });

      if (response.updated && response.updated.length > 0) {
        setSuccess(`Successfully transferred ${response.totalUpdated} of ${response.totalRequested} student(s) to ${selectedTargetClass.name}`);

        if (response.notFound && response.notFound.length > 0) {
          setError(`Warning: ${response.notFound.length} student(s) could not be updated.`);
        }
      } else {
        setError("No students were transferred. Please try again.");
      }

      setSelectedStudents([]);
      setSelectedTargetClass(null);

      // Reload data to reflect changes
      await loadData();

    } catch (err) {
      setError("Failed to transfer students. Please try again.");
      console.error("Transfer error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStudents([]);
    setSelectedTargetClass(null);
    setError("");
    setSuccess("");
    setStudentGradeFilter("all");
    setClassGradeFilter("all");
    setClassAcademicYearFilter("all");
    setClassSnapshotFilter("all");
    setStudentSearchTerm("");
    setClassSearchTerm("");
    setShowConfirmModal(false);
    onHide();
  };

  const filteredStudents = getFilteredStudents();
  const filteredClasses = getFilteredClasses();

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUsers className="me-2" />
          Group Student Transfers
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: '600px' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row className="h-100">
          {/* Left Panel - Students */}
          <Col md={6} className="border-end">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                Students
                <Badge bg="primary" className="ms-2">{selectedStudents.length} selected</Badge>
              </h5>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleSelectAllStudents}
                disabled={filteredStudents.length === 0}
              >
                {selectedStudents.length === filteredStudents.length ? "Deselect All" : "Select All"}
              </Button>
            </div>

            {/* Student Filters */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Select
                  value={studentGradeFilter}
                  onChange={(e) => setStudentGradeFilter(e.target.value)}
                  size="sm"
                >
                  {gradeLevels.map(grade => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search students, classes..."
                    value={studentSearchTerm}
                    onChange={(e) => setStudentSearchTerm(e.target.value)}
                    size="sm"
                  />
                  <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </div>
              </Col>
            </Row>

            {/* Students List */}
            <div style={{ height: '400px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-4">Loading students...</div>
              ) : filteredStudents.length === 0 ? (
                <div className="text-center py-4 text-muted">No students found</div>
              ) : (
                <ListGroup>
                  {filteredStudents.map(student => (
                    <ListGroup.Item
                      key={student.id}
                      className={`d-flex justify-content-between align-items-center cursor-pointer ${selectedStudents.find(s => s.id === student.id) ? 'bg-light border-primary' : ''
                        }`}
                      onClick={() => handleStudentSelect(student)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div>
                        <div className="fw-medium">
                          {student.firstName && student.lastName
                            ? `${student.lastName}, ${student.firstName}`
                            : student.name}
                        </div>
                        <small className="text-muted">
                          Current Class: {student.className} ({student.grade})
                        </small>
                      </div>
                      {selectedStudents.find(s => s.id === student.id) && (
                        <FaCheck className="text-primary" />
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          </Col>

          {/* Right Panel - Classes */}
          <Col md={6}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Target Classes</h5>
              {selectedTargetClass && (
                <Badge bg="success">
                  Selected: {selectedTargetClass.name}
                </Badge>
              )}
            </div>

            {/* Class Filters */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Select
                  value={classAcademicYearFilter}
                  onChange={(e) => setClassAcademicYearFilter(e.target.value)}
                  size="sm"
                >
                  {availableAcademicYears.map(year => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={classGradeFilter}
                  onChange={(e) => setClassGradeFilter(e.target.value)}
                  size="sm"
                >
                  {gradeLevels.map(grade => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={classSnapshotFilter}
                  onChange={(e) => setClassSnapshotFilter(e.target.value)}
                  size="sm"
                >
                  {snapshotOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search classes..."
                    value={classSearchTerm}
                    onChange={(e) => setClassSearchTerm(e.target.value)}
                    size="sm"
                  />
                  <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </div>
              </Col>
            </Row>

            {/* Classes List */}
            <div style={{ height: '400px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-4">Loading classes...</div>
              ) : filteredClasses.length === 0 ? (
                <div className="text-center py-4 text-muted">No classes found</div>
              ) : (
                <div className="d-grid gap-2">
                  {filteredClasses.map(classItem => (
                    <Card
                      key={classItem.id}
                      className={`cursor-pointer ${selectedTargetClass?.id === classItem.id ? 'border-success bg-light' : ''
                        }`}
                      onClick={() => handleClassSelect(classItem)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Body className="py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center">
                              <div className="fw-medium me-2">{classItem.name}</div>
                              {classItem.snapshot && (
                                <Badge bg="secondary" className="me-2">
                                  <FaCamera className="me-1" size={10} />
                                  Snapshot
                                </Badge>
                              )}
                            </div>
                            <small className="text-muted">
                              {classItem.grade}
                              {classItem.teacher && ` • ${classItem.teacher}`}
                              {classItem.academicYear && ` • ${classItem.academicYear}`}
                            </small>
                          </div>
                          <div className="text-end">
                            {selectedTargetClass?.id === classItem.id && (
                              <FaCheck className="text-success" />
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {selectedStudents.length > 0 && selectedTargetClass && (
              <span>
                <FaArrowRight className="me-1" />
                Moving {selectedStudents.length} student(s) to {selectedTargetClass.name}
                {selectedTargetClass.snapshot && (
                  <Badge bg="warning" className="ms-2 text-dark">
                    <FaCamera className="me-1" size={10} />
                    Snapshot Class
                  </Badge>
                )}
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleTransferClick}
              disabled={selectedStudents.length === 0 || !selectedTargetClass || loading}
            >
              {loading ? "Transferring..." : "Transfer Students"}
            </Button>
          </div>
        </div>
      </Modal.Footer>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="d-flex align-items-center">
            <FaUsers className="me-2" />
            Confirm Student Transfer
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: '60px', height: '60px' }}>
              <FaArrowRight className="text-primary" size={24} />
            </div>
            <h5 className="mb-2">Transfer {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}</h5>
            <p className="text-muted mb-0">Please review the details below before confirming</p>
          </div>

          {/* Students List */}
          <div className="mb-4">
            <h6 className="mb-3 d-flex align-items-center">
              <Badge bg="primary" className="me-2">{selectedStudents.length}</Badge>
              Selected Students
            </h6>
            <div className="border rounded-3 bg-light p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {selectedStudents.map((student, index) => (
                <div key={student.id} className="d-flex justify-content-between align-items-center py-2 px-2 mb-2 bg-white rounded border-start border-primary border-3"
                  style={{ borderStartWidth: '4px !important' }}>
                  <div>
                    <div className="fw-medium text-dark">
                      {student.firstName && student.lastName
                        ? `${student.lastName}, ${student.firstName}`
                        : student.name}
                    </div>
                    <small className="text-muted">Currently in: {student.className}</small>
                  </div>
                  <Badge bg="outline-secondary" className="text-muted">{student.grade}</Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Transfer Details */}
          <div className="rounded-3 p-4 mb-4"
            style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
            <div className="row align-items-center">
              <div className="col-5">
                <div className="text-center">
                  <div className="text-muted mb-1">FROM</div>
                  <div className="fw-medium">Current Classes</div>
                  <small className="text-muted">Multiple classes</small>
                </div>
              </div>
              <div className="col-2 text-center">
                <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}>
                  <FaArrowRight className="text-white" size={16} />
                </div>
              </div>
              <div className="col-5">
                <div className="text-center">
                  <div className="text-muted mb-1">TO</div>
                  <div className="fw-bold text-success d-flex align-items-center justify-content-center">
                    {selectedTargetClass?.name}
                    {selectedTargetClass?.snapshot && (
                      <Badge bg="secondary" className="ms-2">
                        <FaCamera className="me-1" size={10} />
                        Snapshot
                      </Badge>
                    )}
                  </div>
                  <div className="small text-muted">
                    {selectedTargetClass?.grade}
                    {selectedTargetClass?.academicYear && ` • ${selectedTargetClass.academicYear}`}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Warning/Info */}
          <div className={`alert ${selectedTargetClass?.snapshot ? 'alert-warning' : 'alert-info'} d-flex align-items-start`} role="alert">
            <div className="me-2">{selectedTargetClass?.snapshot ? '⚠️' : 'ℹ️'}</div>
            <div>
              <strong>{selectedTargetClass?.snapshot ? 'Snapshot Warning:' : 'Important:'}</strong>
              {selectedTargetClass?.snapshot
                ? ' You are transferring students to a snapshot class. Snapshot classes are typically historical records and may not be intended for active student assignments.'
                : ' This action will move all selected students to the target class. This change cannot be undone easily. Please ensure you have selected the correct target class.'
              }
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light border-0 p-4">
          <div className="d-flex w-100 justify-content-between align-items-center">
            <div className="text-muted">
              <small>
                <strong>{selectedStudents.length}</strong> student{selectedStudents.length !== 1 ? 's' : ''} will be transferred
              </small>
            </div>
            <div>
              <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)} className="me-3">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleConfirmTransfer} size="lg" className="px-4">
                Confirm Transfer
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default GroupStudentTransfersModal;